import React from 'react'
import { TransactionEvent } from '../events/event.types'
import { HistoryTabTranslations } from '../HistoryTab.types'
import { getDateLocale } from '../events/formatUtils'
import styled from 'styled-components'

interface TransactionDetailProps {
  event: TransactionEvent
  formatPrice?: (price: number) => string
  formatPricePerSqm?: (price: number) => string
  formatDate?: (date: string) => string
  translations?: HistoryTabTranslations
}

const DetailContainer = styled.div`
  padding: ${({ theme }) => theme.spacing.large};
`

const Section = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.large};
`

const SectionTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.section};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 ${({ theme }) => theme.spacing.medium} 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
`

const DataGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${({ theme }) => theme.spacing.medium};
`

const DataRow = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.small};
`

const DataLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing.micro};
`

const DataValue = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`

const PriceValue = styled(DataValue)`
  font-size: ${({ theme }) => theme.typography.fontSize.title};
  color: ${({ theme }) => theme.colors.primary};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
`

const Badge = styled.span`
  display: inline-block;
  padding: ${({ theme }) => theme.spacing.micro}
    ${({ theme }) => theme.spacing.small};
  background: ${({ theme }) => theme.colors.primaryLight};
  color: ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`

const ParticipantsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${({ theme }) => theme.spacing.medium};
`

const ParticipantCard = styled.div`
  padding: ${({ theme }) => theme.spacing.medium};
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
`

const ParticipantName = styled.div`
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.xsmall};
`

const ParticipantDetail = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing.micro};
`

const PropertyCard = styled.div`
  padding: ${({ theme }) => theme.spacing.medium};
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  margin-bottom: ${({ theme }) => theme.spacing.medium};
`

const PropertyHeader = styled.div`
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.small};
`

export const TransactionDetail: React.FC<TransactionDetailProps> = ({
  event,
  formatPrice,
  formatDate,
  translations,
}) => {
  const defaultFormatPrice = (price?: number) => {
    if (!price) return '—'
    return new Intl.NumberFormat('en-CH', {
      style: 'currency',
      currency: 'CHF',
      minimumFractionDigits: 0,
    }).format(price)
  }

  const defaultFormatDate = (dateString?: string) => {
    if (!dateString) return '—'
    try {
      return new Date(dateString).toLocaleDateString(getDateLocale(), {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    } catch {
      return '—'
    }
  }

  const priceFormatter = formatPrice || defaultFormatPrice
  const dateFormatter = formatDate || defaultFormatDate

  return (
    <DetailContainer>
      {/* Main Transaction Information */}
      <Section>
        <SectionTitle>
          {translations?.sections?.transactionInfo || 'Transaction Information'}
        </SectionTitle>
        <DataGrid>
          {event.transaction_total_price != null && (
            <DataRow>
              <DataLabel>
                {translations?.fields?.totalPrice || 'Total Price'}
              </DataLabel>
              <PriceValue>
                {priceFormatter(event.transaction_total_price)}
              </PriceValue>
            </DataRow>
          )}
          {event.transaction_date && (
            <DataRow>
              <DataLabel>
                {translations?.fields?.transactionDate || 'Transaction Date'}
              </DataLabel>
              <DataValue>{dateFormatter(event.transaction_date)}</DataValue>
            </DataRow>
          )}
          {event.transaction_id && (
            <DataRow>
              <DataLabel>
                {translations?.fields?.transactionId || 'Transaction ID'}
              </DataLabel>
              <DataValue>{event.transaction_id}</DataValue>
            </DataRow>
          )}
          {event.official_id && (
            <DataRow>
              <DataLabel>
                {translations?.fields?.officialId || 'Official ID'}
              </DataLabel>
              <DataValue>{event.official_id}</DataValue>
            </DataRow>
          )}
          {event.transaction_step_name && (
            <DataRow>
              <DataLabel>
                {translations?.fields?.transactionType || 'Transaction Type'}
              </DataLabel>
              <DataValue>
                <Badge>{event.transaction_step_name}</Badge>
              </DataValue>
            </DataRow>
          )}
          {event.publication_date && (
            <DataRow>
              <DataLabel>
                {translations?.fields?.publicationDate || 'Publication Date'}
              </DataLabel>
              <DataValue>{dateFormatter(event.publication_date)}</DataValue>
            </DataRow>
          )}
        </DataGrid>

        {event.transaction_step_text && (
          <DataRow style={{ marginTop: '16px' }}>
            <DataLabel>
              {translations?.fields?.description ||
                translations?.description ||
                'Description'}
            </DataLabel>
            <DataValue>{event.transaction_step_text}</DataValue>
          </DataRow>
        )}
      </Section>

      {/* Participants */}
      {event.transaction_participants &&
        event.transaction_participants.length > 0 && (
          <Section>
            <SectionTitle>
              {translations?.sections?.participants || 'Participants'} (
              {event.transaction_participants.length})
            </SectionTitle>
            <ParticipantsList>
              {event.transaction_participants.map((participant: any, idx) => (
                <ParticipantCard key={idx}>
                  <ParticipantName>
                    {participant.name ||
                      `${translations?.fields?.participant || 'Participant'} ${idx + 1}`}
                  </ParticipantName>
                  {participant.role && (
                    <ParticipantDetail>
                      <strong>{translations?.fields?.role || 'Role'}:</strong>{' '}
                      {participant.role}
                    </ParticipantDetail>
                  )}
                  {participant.participant_type && (
                    <ParticipantDetail>
                      <strong>
                        {translations?.fields?.participantType || 'Type'}:
                      </strong>{' '}
                      {participant.participant_type}
                    </ParticipantDetail>
                  )}
                  {participant.share && (
                    <ParticipantDetail>
                      <strong>{translations?.fields?.share || 'Share'}:</strong>{' '}
                      {participant.share}
                    </ParticipantDetail>
                  )}
                </ParticipantCard>
              ))}
            </ParticipantsList>
          </Section>
        )}

      {/* Properties */}
      {event.transaction_properties &&
        event.transaction_properties.length > 0 && (
          <Section>
            <SectionTitle>
              {translations?.sections?.properties || 'Properties'} (
              {event.transaction_properties.length})
            </SectionTitle>
            {event.transaction_properties.map((property: any, idx) => (
              <PropertyCard key={idx}>
                <PropertyHeader>
                  {translations?.fields?.property || 'Property'} {idx + 1}
                </PropertyHeader>
                <DataGrid>
                  {property.city_name && (
                    <DataRow>
                      <DataLabel>
                        {translations?.fields?.city || 'City'}
                      </DataLabel>
                      <DataValue>{property.city_name}</DataValue>
                    </DataRow>
                  )}
                  {property.npa_name && (
                    <DataRow>
                      <DataLabel>
                        {translations?.fields?.postalCode || 'Postal Code'}
                      </DataLabel>
                      <DataValue>{property.npa_name}</DataValue>
                    </DataRow>
                  )}
                  {property.land_area && (
                    <DataRow>
                      <DataLabel>
                        {translations?.fields?.landArea ||
                          translations?.landArea ||
                          'Land Area'}
                      </DataLabel>
                      <DataValue>{property.land_area} m²</DataValue>
                    </DataRow>
                  )}
                  {property.surface_area_total && (
                    <DataRow>
                      <DataLabel>
                        {translations?.fields?.totalSurface || 'Total Surface'}
                      </DataLabel>
                      <DataValue>{property.surface_area_total} m²</DataValue>
                    </DataRow>
                  )}
                  {property.legal_type && (
                    <DataRow>
                      <DataLabel>
                        {translations?.fields?.legalType || 'Legal Type'}
                      </DataLabel>
                      <DataValue>{property.legal_type}</DataValue>
                    </DataRow>
                  )}
                  {property.property_number && (
                    <DataRow>
                      <DataLabel>
                        {translations?.fields?.propertyNumber ||
                          'Property Number'}
                      </DataLabel>
                      <DataValue>{property.property_number}</DataValue>
                    </DataRow>
                  )}
                </DataGrid>
              </PropertyCard>
            ))}
          </Section>
        )}

      {/* Counts Summary */}
      {(event.buyers_count ||
        event.sellers_count ||
        event.properties_count) && (
        <Section>
          <SectionTitle>
            {translations?.sections?.summary || 'Summary'}
          </SectionTitle>
          <DataGrid>
            {event.buyers_count !== undefined && (
              <DataRow>
                <DataLabel>
                  {translations?.fields?.numberOfBuyers || 'Number of Buyers'}
                </DataLabel>
                <DataValue>{event.buyers_count}</DataValue>
              </DataRow>
            )}
            {event.sellers_count !== undefined && (
              <DataRow>
                <DataLabel>
                  {translations?.fields?.numberOfSellers || 'Number of Sellers'}
                </DataLabel>
                <DataValue>{event.sellers_count}</DataValue>
              </DataRow>
            )}
            {event.properties_count !== undefined && (
              <DataRow>
                <DataLabel>
                  {translations?.fields?.numberOfProperties ||
                    'Number of Properties'}
                </DataLabel>
                <DataValue>{event.properties_count}</DataValue>
              </DataRow>
            )}
          </DataGrid>
        </Section>
      )}
    </DetailContainer>
  )
}
